import { useScienceTutorSection } from './scienceTutorContext';
import { CircleAlert } from 'lucide-react';
import { useState } from 'react';

import {
  ALKALI_METALS,
  ALKALI_OBSERVATION_LABELS,
  ALKALI_OBSERVATION_ORDER,
  PREDICTION_ONLY_NOTICE,
  alkaliById,
  alkaliTrendExplanation,
  alkaliWaterEquation,
  alkaliWordEquation,
  checkAlkaliObservation,
  floatsOnWater,
  formatEquation,
  questionsFor,
  type AlkaliId,
  type AlkaliObservationId,
} from './groupReactivity';
import { FeedbackPanel, QuestionList, ShellDiagram, TrendBars } from './GroupReactivityVisuals';

export function AlkaliMode() {
  const [metalId, setMetalId] = useState<AlkaliId>('Li');
  const [choice, setChoice] = useState<AlkaliObservationId | ''>('');
  const [checked, setChecked] = useState(false);
  useScienceTutorSection('AlkaliMode', { metalId, choice, checked });
  const metal = alkaliById(metalId);
  const result = checked && choice ? checkAlkaliObservation(metal, choice) : null;
  const equation = alkaliWaterEquation(metal);

  return (
    <div className="space-y-6">
      <div className="grid gap-6 xl:grid-cols-[minmax(0,1fr)_minmax(0,1.2fr)]">
        <section aria-label="Choose a group 1 metal" className="min-w-0 rounded-[2rem] border border-hairline bg-canvas-pure p-5 shadow-card-soft sm:p-6">
          <p className="text-xs font-extrabold uppercase tracking-[0.14em] text-brand-sky">1. Choose a metal (group 1)</p>
          <div className="mt-3 grid grid-cols-3 gap-2 sm:grid-cols-5" role="group" aria-label="Group 1 metals">
            {ALKALI_METALS.map((option) => (
              <button
                key={option.id}
                type="button"
                aria-pressed={metalId === option.id}
                aria-label={option.name}
                onClick={() => {
                  setMetalId(option.id);
                  setChoice('');
                  setChecked(false);
                }}
                className={`rounded-2xl border px-3 py-2.5 text-center text-sm font-extrabold transition-colors motion-reduce:transition-none ${
                  metalId === option.id
                    ? 'border-brand-sky bg-wash-sky text-ink'
                    : 'border-hairline bg-white text-ink-soft hover:border-brand-sky/40'
                }`}
              >
                <span className="block text-lg">{option.id}</span>
                <span className="block text-xs font-bold text-slate2">{option.schoolDemo ? option.name : 'predict only'}</span>
              </button>
            ))}
          </div>

          <div className="mt-5 grid gap-4 sm:grid-cols-[minmax(0,13rem)_minmax(0,1fr)] sm:items-center">
            <ShellDiagram
              symbol={metal.id}
              atomicNumber={metal.atomicNumber}
              shells={metal.shells}
              label={`Shell diagram of ${metal.name}: electron arrangement ${metal.shells.join(', ')}; one outer electron in shell ${metal.shells.length}`}
            />
            <div className="text-sm font-medium leading-6 text-ink-soft">
              <p className="font-extrabold text-ink">
                {metal.name.charAt(0).toUpperCase() + metal.name.slice(1)}: {metal.shells.join(', ')}
              </p>
              <p className="mt-1">{alkaliTrendExplanation(metal)}</p>
            </div>
          </div>

          {!metal.schoolDemo ? (
            <div role="note" className="mt-4 flex gap-3 rounded-2xl border border-brand-coral/45 bg-wash-coral p-4">
              <CircleAlert className="mt-0.5 h-5 w-5 shrink-0 text-brand-coral" aria-hidden="true" />
              <p className="text-sm font-extrabold leading-6 text-ink">{PREDICTION_ONLY_NOTICE}</p>
            </div>
          ) : null}
        </section>

        <section aria-label="Predict the reaction with water" className="min-w-0 rounded-[2rem] border border-hairline bg-canvas-pure p-5 shadow-card-soft sm:p-6">
          <p className="text-xs font-extrabold uppercase tracking-[0.14em] text-brand-coral">
            2. Predict: a small piece of {metal.name} is dropped into water
          </p>
          <div className="mt-3 grid gap-2" role="group" aria-label="Predict what you would see">
            {ALKALI_OBSERVATION_ORDER.map((option) => (
              <button
                key={option}
                type="button"
                aria-pressed={choice === option}
                onClick={() => {
                  setChoice(option);
                  setChecked(false);
                }}
                className={`rounded-2xl border px-4 py-2.5 text-left text-sm font-extrabold transition-colors motion-reduce:transition-none ${
                  choice === option ? 'border-brand-coral bg-wash-coral text-ink' : 'border-hairline bg-white text-ink-soft hover:border-brand-coral/40'
                }`}
              >
                {ALKALI_OBSERVATION_LABELS[option]}
              </button>
            ))}
          </div>
          <button
            type="button"
            disabled={!choice}
            onClick={() => setChecked(true)}
            className="mt-3 rounded-full bg-ink px-5 py-2.5 text-sm font-extrabold text-white disabled:cursor-not-allowed disabled:opacity-40"
          >
            {metal.schoolDemo ? 'Check prediction' : 'Check against the trend'}
          </button>

          {result ? (
            <div className="mt-4 space-y-4">
              <FeedbackPanel label="Water reaction feedback" result={result} />
              <div className="rounded-2xl border border-hairline bg-white p-4">
                <p className="text-xs font-extrabold uppercase tracking-wide text-slate2">
                  {metal.schoolDemo ? 'Teacher demonstration: what you would see' : 'Predicted from the trend (not done in schools)'}
                </p>
                <ul className="mt-2 list-disc space-y-1 pl-5 text-sm font-medium leading-6 text-ink-soft">
                  {metal.observations.map((observation) => (
                    <li key={observation}>{observation}</li>
                  ))}
                </ul>
                <p className="mt-2 text-xs font-bold text-slate2">
                  Density {metal.densityGcm3} g/cm³ · {floatsOnWater(metal) ? 'floats (less dense than water)' : 'sinks (denser than water)'}
                </p>
              </div>
              <div className="rounded-2xl border border-hairline bg-white p-4">
                <p className="text-xs font-extrabold uppercase tracking-wide text-slate2">Equations</p>
                <p className="mt-1 text-sm font-extrabold text-ink">{alkaliWordEquation(metal)}</p>
                <p className="mt-1 break-words text-base font-extrabold text-ink" aria-label="Alkali metal equation">
                  {formatEquation(equation.reactants)} → {formatEquation(equation.products)}
                </p>
              </div>
            </div>
          ) : null}
        </section>
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <TrendBars
          title="Atomic radius"
          unit="pm"
          bars={ALKALI_METALS.map((entry) => ({ id: entry.id, label: entry.id, value: entry.radiusPm, display: `${entry.radiusPm} pm`, active: entry.id === metal.id }))}
          note="Atoms get bigger down the group: one more shell each time."
        />
        <TrendBars
          title="First ionisation energy"
          unit="kJ/mol"
          bars={ALKALI_METALS.map((entry) => ({ id: entry.id, label: entry.id, value: entry.firstIonisationKjMol, display: `${entry.firstIonisationKjMol}`, active: entry.id === metal.id }))}
          note="Less energy is needed to remove the outer electron down the group, so reactivity increases."
        />
      </div>

      <QuestionList title="Alkali metal predictions" questions={questionsFor('alkali')} />
    </div>
  );
}
